/* eslint-disable ember/no-computed-properties-in-native-classes, ember/classic-decorator-no-classic-methods */
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import dayjs from 'dayjs';
import { timeout } from 'ember-concurrency';
import type { Hotel, Market } from 'frontend-utils/models';
import { b64EncodeUnicode } from 'frontend-utils/utils/base64-encode-decode';
import { DOMAIN_NAME } from 'frontend-utils/utils/constants/domain-utils';
import {
  isPlatformLocalStagingDomain,
  isPlatformProductionDomain,
  isPlatformStagingOrFeatureBranchDomain,
  PLATFORM_PRODUCTION_DOMAIN,
  PLATFORM_STAGING_DOMAIN,
} from 'frontend-utils/utils/domain-checks';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import Cookies from 'js-cookie';
import type { RouterServiceWithLegacyTransitionTo } from 'lumen/initializers/router-legacy-transition-to';
import type { SmartInsightDay } from 'lumen/models';
import {
  SMART_INSIGHT_PRIORITIES,
  type SmartInsightPriority,
} from 'lumen/models/smart-insights/smart-insight-day';
import {
  BI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
  DRAWER_IDS,
  MI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
} from 'lumen/utils/constants/general-constants';
import {
  SMART_INSIGHTS_KPI_COMPARISON_PERIODS,
  SMART_INSIGHTS_TABS,
  SMART_INSIGHTS_VIEW_LOCAL_STORAGE_KEY,
  SMART_INSIGHTS_VIEWS,
  type SmartInsightsKpiComparisonPeriod,
  type SmartInsightsTab,
  type SmartInsightsView,
} from 'lumen/utils/constants/smart-insights';
import {
  TRIAL_INDICATION_LOCALSTORAGE,
  TRIAL_INDICATION_LOCALSTORAGE_EXPIRED_AUTO_OPEN,
  TRIAL_INDICATION_LOCALSTORAGE_KEY,
  TRIAL_INDICATION_LOCALSTORAGE_NEARLY_EXPIRED_AUTO_OPEN,
} from 'lumen/utils/constants/trial-indication-constants';
import createAccountMenu from 'lumen/utils/mount-react/create-account-menu';
import createAccountSelector from 'lumen/utils/mount-react/create-account-selector';
import createStaffAccessProps from 'lumen/utils/mount-react/create-staff-access-props';
import type {
  AccountMenuProps,
  AccountSelectorProps,
  SmartInsightsArchivePayload,
  SmartInsightsProps,
  SmartInsightsStatus,
  StaffAccessIndicatorProps,
} from 'shared-react/lumen';

type Option = {
  action: string;
  icon?: IconName;
  label: string;
};

// Derives the shared-react package's loading/error/success vocabulary from an
// ember-concurrency task, for props (like `upsell.status`) that don't already
// have a dedicated tracked flag the way `smartInsights.reactStatus` does.
export function taskToReactStatus(task: {
  isRunning: boolean;
  last?: { isError: boolean } | null;
}): SmartInsightsStatus {
  if (!task.last) {
    return 'loading';
  }
  return task.last.isError ? 'error' : 'success';
}

export default class HotelController extends Controller {
  queryParams = [
    'smartInsightsDay',
    'smartInsightsExpanded',
    'smartInsightsKpiComparisonPeriod',
    'smartInsightsPriorities',
    'smartInsightsTab',
    'smartInsightsView',
  ];

  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare drawer: Services['drawer'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare navigation: Services['navigation'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare roiTrophies: Services['roi-trophies'];
  @service declare router: RouterServiceWithLegacyTransitionTo;
  @service declare smartInsights: Services['smart-insights'];
  @service('smart-insights/upsell')
  declare smartInsightsUpsell: Services['smart-insights/upsell'];
  @service declare usageAnalytics: Services['usage-analytics'];
  @service declare usersnap: Services['usersnap'];

  @tracked smartInsightsDay: string | null = null;
  @tracked smartInsightsExpanded: boolean = false;
  @tracked smartInsightsKpiComparisonPeriod: SmartInsightsKpiComparisonPeriod =
    SMART_INSIGHTS_KPI_COMPARISON_PERIODS.YESTERDAY;
  @tracked smartInsightsPriorities: string = [
    SMART_INSIGHT_PRIORITIES.HIGH,
    SMART_INSIGHT_PRIORITIES.MEDIUM,
    SMART_INSIGHT_PRIORITIES.LOW,
  ].join(',');
  @tracked smartInsightsTab: SmartInsightsTab = SMART_INSIGHTS_TABS.ACTIVE;
  @tracked smartInsightsView: SmartInsightsView = SMART_INSIGHTS_VIEWS.LIST;

  active = false;
  currentHotelId = null;

  get accountMenuOptions(): Omit<AccountMenuProps, 'kind'> {
    return createAccountMenu(this);
  }

  get accountSelectorOptions(): AccountSelectorProps | undefined {
    const hotel = this.hotelAndMarketService.hotel;

    if (!hotel?.id) {
      return undefined;
    }

    return createAccountSelector({
      current: {
        type: 'hotel',
        id: hotel.id,
        hotelinfoId: Number(hotel.hotelinfo.id),
        name: hotel.hotelinfo.name,
        brandCode: hotel.hotelinfo.brandCode,
        plans: [],
        products: [],
      },
      ajax: this.ajax,
      router: this.router,
      access: this.access,
      errorTracking: this.errorTracking,
      isDemoMode: this.isDemoMode,
    });
  }

  get hasStaffAccessIndicator(): boolean {
    if (this.isDemoMode) {
      return false;
    }

    // When Unified Hotel View is on, subscriptions are loaded into navigation
    // so we can check across all of them. Otherwise fall back to the active
    // subscription via the access service.
    if (this.navigation.hasUnifiedHotelView) {
      return this.navigation.hasAnyRestrictedOrDeniedFeatures;
    }

    return this.access.hasRestrictedFeatures || this.access.hasDeniedFeatures;
  }

  get hostDomain(): string {
    return this.ajax.host!;
  }

  @computed('hotelAndMarketService.hotelOrMarket')
  get hotelOrMarket(): Hotel | Market {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  get isAccountButtonActive(): boolean {
    return this.router.currentRouteName === 'hotel.revpak-exports';
  }

  get isDemoMode(): boolean {
    return Boolean(Cookies.get('demoModev3'));
  }

  get isHappyHolidays(): boolean {
    const weekOfYear = dayjs().week();

    return weekOfYear === 51 || weekOfYear === 52;
  }

  get isSettingsButtonActive(): boolean {
    return (
      this.router.currentRouteName!.includes('settings') &&
      !this.isAccountButtonActive
    );
  }

  get priorities(): SmartInsightPriority[] {
    if (!this.smartInsightsPriorities) {
      return [];
    }
    return this.smartInsightsPriorities.split(',') as SmartInsightPriority[];
  }

  // Builds the props for the React-mounted `SmartInsights` (behind
  // FEATURE_TOGGLES.SMART_INSIGHTS_REACT, see hotel.gts). lumen's `access`
  // service doesn't have `hasBookings`/`hasOverview`/`hasRateInsight` under
  // those names — they're the same features as `hasRevenueInsight`/
  // `hasOverviewDashboardTab`/`hasRates` respectively (verified against the
  // package's source-link.ts and lumen's own SmartInsightDayInsightEntryModel).
  get reactSmartInsightsProps(): SmartInsightsProps {
    const {
      access,
      hotelAndMarketService,
      smartInsights,
      smartInsightsUpsell,
    } = this;

    return {
      access: {
        hasBenchmarkInsight: access.hasBenchmarkInsight,
        hasBookings: access.hasRevenueInsight,
        hasEvents: access.hasEvents,
        hasMarketInsight: access.hasMarketInsight,
        hasOverview: access.hasOverviewDashboardTab,
        hasRateInsight: access.hasRates,
        hasRevenueInsight: access.hasRevenueInsight,
        hasSmartInsights: access.hasSmartInsights,
        hasSmartInsightsUpsell: access.hasSmartInsightsUpsell,
        hasSmartInsightsYearView: access.hasSmartInsightsYearView,
        hasSpider: access.hasSpider,
      },
      archivedDay: smartInsights.reactArchivedDay,
      currency: hotelAndMarketService.hotel?.currencyCode ?? 'USD',
      data: smartInsights.reactData,
      hotelId: hotelAndMarketService.hotel?.id ?? '',
      hotelTimezone: hotelAndMarketService.timezone ?? 0,
      isArchiving: smartInsights.archiveInsightTask.isRunning,
      isGenerating: smartInsights.getSmartInsightsTask.isRunning,
      isOpen: this.smartInsightsExpanded,
      kpiComparisonPeriod: this.smartInsightsKpiComparisonPeriod,
      onAnalyticsEvent: (event, payload) =>
        this.usageAnalytics.track(event, {
          eventTrigger: 'smart-insights-react',
          ...payload,
        }),
      onArchive: (payload) => this.archiveFromReact(payload),
      onClose: () => void this.toggleSmartInsightsDrawer(),
      onFeedbackEvent: (usersnapEvent) => this.usersnap.logEvent(usersnapEvent),
      onKpiComparisonPeriodChange: (period) =>
        this.setSmartInsightsKpiComparisonPeriod(period),
      onPrioritiesChange: (priorities) =>
        this.setSmartInsightsPriorities([...priorities]),
      onSelectedDayChange: (day) => this.setSmartInsightsDay(day),
      onTabChange: (tab) => this.setSmartInsightsTab(tab),
      onViewChange: (view) => this.setSmartInsightsView(view),
      priorities: this.priorities,
      selectedDay: this.smartInsightsDay,
      status: smartInsights.reactStatus,
      tab: this.smartInsightsTab,
      upsell: {
        isSubmitting: smartInsightsUpsell.saveUpsellRecordTask.isRunning,
        onGetInTouch: () => void smartInsightsUpsell.onClickGetInTouch(),
        status: taskToReactStatus(smartInsightsUpsell.upsellApiResponseTask),
        trialRequested: smartInsightsUpsell.hasRequestedTrial,
      },
      view: this.smartInsightsView,
    };
  }

  get selectedSmartInsight(): SmartInsightDay | null {
    if (!this.smartInsightsDay) {
      return null;
    }

    return (
      this.smartInsights.smartInsightDays.find(
        (day) => day.stayDate === this.smartInsightsDay,
      ) ?? null
    );
  }

  get settingsOptions(): Option[] {
    const options: Option[] = [
      {
        label: this.intl.t('menu.my_account'),
        action: 'redirectToMyAccount',
        icon: 'circle-user',
      },
      {
        label: this.intl.t('menu.help'),
        action: 'openHelpCenter',
        icon: 'circle-question-mark',
      },
    ];

    if (
      this.hotelAndMarketService.hotel &&
      this.access.hasSpiderRevpakLibrary
    ) {
      options.splice(1, 0, {
        label: this.intl.t('menu.spider.revpak-library'),
        action: 'redirectToSpiderHotelRevpak',
        icon: 'bookmark-filled',
      });
    }

    // Insert refer a friend link at 3rd position, in case of no rebranding
    if (!this.hotelOrMarket.hasRebranding) {
      options.splice(2, 0, {
        label: this.intl.t('menu.refer_a_friend'),
        action: 'redirectReferAFriend',
        icon: 'refer-friend',
      });
    }

    options.push({
      label: this.intl.t('menu.logout'),
      action: 'logout',
      icon: 'logout',
    });

    if (this.access.isStaffUser) {
      options.push({
        label: this.intl.t('menu.admin'),
        action: 'redirectToAdmin',
      });
      options.push({
        label: this.intl.t('menu.admin_subscription'),
        action: 'redirectToSubscription',
      });
    }

    if (this.access.hasFeature('user.app_demo_mode')) {
      if (this.isDemoMode) {
        options.push({
          label: this.intl.t('menu.disable_demo'),
          action: 'disableDemo',
        });
      } else {
        options.push({
          label: this.intl.t('menu.enable_demo'),
          action: 'enableDemo',
        });
      }
    }

    return options;
  }

  get showEmptyState(): boolean {
    const isSettingsPage = this.router.currentRouteName!.includes('.settings.');

    return this.access.isContextHiddenForStaffUsers && !isSettingsPage;
  }

  get showSmartInsights(): boolean {
    return (
      this.router.currentRouteName!.startsWith('hotel.') &&
      this.access.hasSmartInsights
    );
  }

  get staffAccessOptions(): StaffAccessIndicatorProps {
    return createStaffAccessProps(this);
  }

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    this.drawer.registerDrawer(DRAWER_IDS.SMART_INSIGHTS_DRAWER);
  }

  @action
  archiveFromReact(payload: SmartInsightsArchivePayload): void {
    void this.smartInsights.archiveInsightTask.perform(
      payload.stayDate,
      payload.reason,
      this.hotelAndMarketService.hotel?.id ?? undefined,
      undefined,
      payload,
    );
  }

  @action
  disableDemo(): void {
    Cookies.remove('demoModev3');
    window.location.reload();
  }

  @action
  enableDemo(): void {
    Cookies.set(
      'demoModev3',
      b64EncodeUnicode(
        JSON.stringify({
          active: true,
          map: [],
          hotelinfo: this.hotelAndMarketService.hotel?.hotelinfo?.name,
        }),
      ),
    );
    window.location.reload();
  }

  // The React drawer (behind `smart-insights-react`) is a real flex sibling
  // of #content-container, so landing on a page with it already expanded
  // (e.g. `?smartInsightsExpanded=true` in a shared link) shrinks the
  // content to its final width only once React mounts the panel -- a beat
  // after charts already measured their (stale, full-width) container on
  // first render. The legacy Ember drawer never needed this: it's always a
  // fixed overlay, and its own push effect is a CSS class applied
  // synchronously from first paint. No 500ms wait here since there's no
  // open/close transition to land on cold load -- the panel renders at its
  // final width immediately.
  @action
  handleReactSmartInsightsMountInsert(): void {
    if (this.smartInsightsExpanded) {
      window.dispatchEvent(new Event('resize'));
    }
  }

  @action
  logout(): void {
    // Segment stores personal info in localstorage
    // Remove it before logging out
    this.localStorage.removeItem('ajs_user_traits');
    this.localStorage.removeItem(TRIAL_INDICATION_LOCALSTORAGE);
    this.localStorage.removeItem(TRIAL_INDICATION_LOCALSTORAGE_KEY);
    this.localStorage.removeItem(
      TRIAL_INDICATION_LOCALSTORAGE_NEARLY_EXPIRED_AUTO_OPEN,
    );
    this.localStorage.removeItem(
      TRIAL_INDICATION_LOCALSTORAGE_EXPIRED_AUTO_OPEN,
    );
    this.localStorage.removeItem(BI_UPSELL_CONTACT_LOCALSTORAGE_KEY);
    this.localStorage.removeItem(MI_UPSELL_CONTACT_LOCALSTORAGE_KEY);

    // Clear firebase auth data
    indexedDB.deleteDatabase('firebaseLocalStorageDb');

    this.ajax
      .post('/logout/')
      .then(() => {
        window.location.href = '/login';
      })
      .catch(() => {
        window.location.href = '/login';
      });
  }

  @action
  openHelpCenter(): void {
    this.intercom.redirectToIntercomHelp();
  }

  @action
  performAction(option: Option): void {
    this.send(option.action);
  }

  @action
  redirectReferAFriend(): void {
    const extraData = [
      {
        name: 'referrer_first_name',
        value: this.hotelAndMarketService.user?.firstName,
      },
      {
        name: 'referrer_last_name',
        value: this.hotelAndMarketService.user?.lastName,
      },
      {
        name: 'referrer_email',
        value: this.hotelAndMarketService.user?.email,
      },
      {
        name: 'referrer_email',
        value: this.hotelAndMarketService.user?.email,
      },
      {
        name: 'referrer_property_name',
        value: this.hotelAndMarketService.hotelOrMarket?.hotelinfo?.name,
      },
    ];

    // Refer a friend menu item is not visible for resellers, so no extra checks required here
    const baseUrl = `https://www.${DOMAIN_NAME}/lp/refer-a-friend`;

    // form a string containing all necessary URL parameters
    let urlParams = '?';

    extraData.forEach((param, index) => {
      urlParams += `${param.name}=${encodeURIComponent(param.value)}`;

      if (index !== extraData.length - 1) {
        urlParams += '&';
      }
    });

    openUrl(`${baseUrl}${urlParams}`);
  }

  @action
  redirectToAdmin(): void {
    let url;

    // Admin only feature, so no further domain checks necessary here
    if (isPlatformProductionDomain()) {
      url = `https://${PLATFORM_PRODUCTION_DOMAIN}/admin/hotel_industry/hotelinfo/${this.hotelAndMarketService.hotel?.hotelinfo?.id}/`;
    } else if (isPlatformStagingOrFeatureBranchDomain()) {
      url = `https://${window.location.hostname}/admin/hotel_industry/hotelinfo/${this.hotelAndMarketService.hotel?.hotelinfo?.id}/`;
    } else if (isPlatformLocalStagingDomain()) {
      url = `https://${PLATFORM_STAGING_DOMAIN}/admin/hotel_industry/hotelinfo/${this.hotelAndMarketService.hotel?.hotelinfo?.id}/`;
    }

    openUrl(url);
  }

  @action
  redirectToMyAccount(): void {
    this.router.legacyTransitionTo('hotel.settings.personal');
  }

  @action
  redirectToSettings(): void {
    this.router.legacyTransitionTo('hotel.settings');
  }

  @action
  redirectToSpiderHotelRevpak(): void {
    this.router.transitionTo(
      'hotel.revpak-exports',
      this.hotelAndMarketService.hotel!.id,
    );
  }

  @action
  redirectToSubscription(): void {
    let url;

    // Admin only feature, so no further domain checks necessary here
    if (isPlatformProductionDomain()) {
      url = `https://${PLATFORM_PRODUCTION_DOMAIN}/admin/subscriptions/subscription/${this.hotelAndMarketService.hotel?.id}/change/`;
    } else if (isPlatformStagingOrFeatureBranchDomain()) {
      url = `https://${window.location.hostname}/admin/subscriptions/subscription/${this.hotelAndMarketService.hotel?.id}/change/`;
    } else if (isPlatformLocalStagingDomain()) {
      url = `https://${PLATFORM_STAGING_DOMAIN}/admin/hotel_industry/hotelinfo/${this.hotelAndMarketService.hotel?.hotelinfo?.id}/`;
    }

    openUrl(url);
  }

  @action
  redirectToTripadvisorManagementConsole(): void {
    window.parent.postMessage(
      {
        action: 'redirect-to-management-center',
      },
      '*',
    );
  }

  @action
  setSmartInsightsDay(day: string | null): void {
    this.smartInsightsDay = day;
  }

  @action
  setSmartInsightsKpiComparisonPeriod(
    period: SmartInsightsKpiComparisonPeriod,
  ): void {
    this.smartInsightsKpiComparisonPeriod = period;
  }

  @action
  setSmartInsightsPriorities(priorities: SmartInsightPriority[]): void {
    this.smartInsightsPriorities = priorities.join(',');
  }

  @action
  setSmartInsightsTab(tab: SmartInsightsTab): void {
    this.smartInsightsTab = tab;
  }

  @action
  setSmartInsightsView(view: SmartInsightsView): void {
    this.smartInsightsView = view;

    this.localStorage.setItem(SMART_INSIGHTS_VIEW_LOCAL_STORAGE_KEY, view);
  }

  @action
  toggleActive(): void {
    this.toggleProperty('active');
  }

  @action
  async toggleSmartInsightsDrawer(): Promise<void> {
    this.drawer.toggleDrawerState(DRAWER_IDS.SMART_INSIGHTS_DRAWER);
    this.smartInsightsExpanded =
      this.drawer.isDrawerExpanded(DRAWER_IDS.SMART_INSIGHTS_DRAWER) ?? false;

    // Trigger window resize after the drawer's 500ms CSS transition so charts
    // (Highcharts) reflow to the new container width.
    await timeout(500);
    window.dispatchEvent(new Event('resize'));
  }
}
